<template>
  <div class="">
    <div v-if="waitingForSessionAttempt" class="alert alert-info my-3" role="alert" >Attaching to your session...</div>
    <div v-else-if="!sessionState" class="alert alert-warning my-3" role="alert" >Please log in as an administrator!</div>
    <div v-else-if="!isAdmin" class="alert alert-warning my-3" role="alert" >you are not an administrator!</div>
    <div v-else class="container">

       <div v-for="server in servers" class="row fs-sm pb-3 gx-0">
        <div class="col-12 col-lg-1 text-center  d-flex flex-row flex-lg-column align-items-center justify-content-center">
          <!-- Changed to Vue-controlled modal triggers -->
          <button class="btn btn-sm text-primary" type="button" @click="openServerLogModal(server)">{{server.name}}</button>
          <button class="btn btn-sm text-primary" type="button" @click="openServerStatusModal(server)">{{server.status}}</button>
          <div class="fs-xs">
            {{errorCount(server)}} Error {{warnCount(server)}} Warn
          </div>
          <button class="btn btn-sm text-primary" type="button" @click="openServerJobViewerModal(server)">{{getActiveJobCount(server)}} Jobs</button>
        </div>
       <div class="col-4 col-lg-1 text-center d-flex flex-column ">
          <div class="mt-auto">{{format(server.monitorData.freememorypercent,1,"%")}}</div>
          <div class="fs-xs">{{formatGb(server.monitorData.freeMemory)}}/{{formatGb(server.monitorData.totalMemory)}}Gb</div>
          <div class="fs-xxs subtitle mt-auto ">Memory</div>
        </div>
        
        <div class="col-4 col-lg-1 text-center d-flex flex-column ">
          <template v-for="(min,i) in statisticsMins">
            <div v-if="server.monitorData.stats" :class="{'mt-auto':i===0}">{{format(server.monitorData.stats[min].errorRate,1,"%")}}</div>
            <div class="ps-1 fs-xxs subtitle">{{min}} min</div>
          </template>
          <div class="fs-xxs subtitle mt-auto ">Errors</div>
        </div>
        <div class="col-4 col-lg-1 text-center d-flex flex-column ">
           <template v-for="(min,i) in statisticsMins">
            <div v-if="server.monitorData.stats"  :class="{'mt-auto':i===0}">{{format(server.monitorData.stats[min].meanTime,0,"ms")}}</div>
            <div class="ps-1 fs-xxs subtitle">{{min}} min</div>
          </template>
          <div class="fs-xxs subtitle mt-auto ">Response (ms)</div>
        </div>
        
        <div class="d-none col-lg-2 text-center d-lg-flex flex-column align-items-center">
          <div v-for="(d,i) in server.monitorData.jobSummary" class="text-nowrap fs-xs" :class="{'mt-auto':i===0}"><a :href="'/internal/reports?uid='+d.id">{{d.id}}</a> ({{d.count}})</div>
          <div class="fs-xxs subtitle mt-auto ">Top users</div>
        </div>
        <div class="d-none col-lg-2 text-center d-lg-flex flex-column align-items-center">
          <div v-for="(d,i) in server.monitorData.jobErrors" class="text-nowrap fs-xs" :class="{'mt-auto':i===0}"><a :href="'/internal/reports?uid='+d.id">{{d.id}}</a> ({{d.count}})</div>
          <div class="fs-xxs subtitle mt-auto ">Error users</div>
        </div>
        
        <div v-bind:class="[server.id]" class="col-12 col-lg-4 text-center">
            <svg class="activitychart"></svg>
        </div>
      </div>
    </div>

    <!-- Server Status Modal with Vue state control -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.serverStatus.isOpen }" 
         :style="{ display: modals.serverStatus.isOpen ? 'block' : 'none' }" 
         tabindex="-1" 
         aria-labelledby="serverStatusModalLabel" 
         :aria-hidden="!modals.serverStatus.isOpen"
         @click.self="closeModal('serverStatus')">
      
      <div v-if="modals.serverStatus.isOpen" class="modal-backdrop fade show" @click="closeModal('serverStatus')"></div>
      
      <div class="modal-dialog modal-dialog-centered modal-fullscreen-sm-down modal-xl">
        <div class="modal-content" style="height:90vh;">
          <div class="modal-header">
            <h5 class="modal-title" id="serverStatusModalLabel">Server status</h5>
            <button type="button" class="btn-close" @click="closeModal('serverStatus')" aria-label="Close"></button>
          </div>
          
          <div class="modal-body overflow-auto">
            <table class="table table-striped table-sm fs-sm">
              <thead>
              <tr><th>Name</th><th>Value</th></tr>    
              </thead>
              <tbody>
                <tr v-for="property in detailedServerStatus">
                  <td>{{property.name}}</td>
                  <td>{{property.value}}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Server Log Modal with Vue state control -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.serverLog.isOpen }" 
         :style="{ display: modals.serverLog.isOpen ? 'block' : 'none' }" 
         tabindex="-1" 
         aria-labelledby="serverLogModalLabel" 
         :aria-hidden="!modals.serverLog.isOpen"
         @click.self="closeModal('serverLog')">
      
      <div v-if="modals.serverLog.isOpen" class="modal-backdrop fade show" @click="closeModal('serverLog')"></div>
      
      <div class="modal-dialog modal-dialog-centered modal-fullscreen-lg-down modal-xl">
        <div class="modal-content" style="height:95vh;">
          <div class="modal-header">
            <h5 class="modal-title" id="serverLogModalLabel">Server Logs</h5>
            <button type="button" class="btn-close" @click="closeModal('serverLog')" aria-label="Close"></button>
          </div>
          
          <div class="modal-body mh-100">
            <div class="row align-items-center ">
              <div class="col-12 col-md-6 d-flex">
                <div class="form-check ps-5 text-nowrap" v-for="(level,i) in ALLLOGLEVELS">
                  <input class="form-check-input" type="checkbox" :value="level" :id="'levelCheck_'+i" v-model="logLevels">
                    <label class="form-check-label" :for="'levelCheck_'+i">
                      {{level}} - {{countLogByLevel(level)}}
                    </label>
                </div>
              </div>
              <div class="col-8 col-md-4 ">
                <input v-model="logFilter" class="form-control form-control-sm" type="text" placeholder="Filter">  
              </div>
              <div class="col-4 col-md-2 fs-xs">
                Showing {{filteredServerLog.length}}/{{serverLog.length}}
                <button class="btn btn-sm btn-secondary p-2" type="button" @click="refreshLog(serverLogServer)" title="Refresh"><i class="bi bi-arrow-counterclockwise"></i></button>
              </div>
            </div>
            
            <div class="table-responsive" style="max-height:90%">
              <table class="table table-striped table-sm fs-sm">
                <thead>
                <tr>
                  <th>DateTime</th>
                  <th>Level</th>
                  <th>Message</th>
                </tr>    
                </thead>
                <tbody>
                  <tr v-for="line in filteredServerLog">
                    <td class="text-nowrap">{{line[0]}}</td>
                    <td>{{line[1]}}</td>
                    <td>{{line[2]}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Server Job Viewer Modal with Vue state control -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.serverJobViewer.isOpen }" 
         :style="{ display: modals.serverJobViewer.isOpen ? 'block' : 'none' }" 
         tabindex="-1" 
         aria-labelledby="serverJobViewerModalLabel" 
         :aria-hidden="!modals.serverJobViewer.isOpen"
         @click.self="closeModal('serverJobViewer')">
      
      <div v-if="modals.serverJobViewer.isOpen" class="modal-backdrop fade show" @click="closeModal('serverJobViewer')"></div>
      
      <div class="modal-dialog modal-dialog-centered modal-fullscreen-sm-down modal-xl">
        <div class="modal-content" style="height:90vh;">
          <div class="modal-header">
            <h5 class="modal-title" id="serverJobViewerModalLabel">Server Jobs</h5>
            <button type="button" class="btn-close" @click="closeModal('serverJobViewer')" aria-label="Close"></button>
          </div>
          <div class="modal-body overflow-auto">
            <div class="d-flex align-items-center justify-content-end fs-xs">
              Showing {{filteredServerJobs.length}}/{{selectedServerJobs.length}}
            </div>
            <table class="table table-striped table-sm fs-sm">
              <thead>
              <tr><th>Info</th><th>Queue</th><th>Execution</th><th>Pool</th><th>Status</th><th>Id</th><th>Name</th></tr>    
              </thead>
              <tbody>
                <tr v-for="d in filteredServerJobs">
                  <td><a :href="'/internal/reports?uid='+d.info">{{d.info}}</a></td>
                  <td>{{d.queueTime}}</td>
                  <td>{{d.execTime}}</td>
                  <td>{{d.pool}}</td>
                  <td>{{getStatusText(d.status)}}</td>
                  <td>{{d.id}}</td>
                  <td>{{d.name}}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <CommonModals />
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_serveradmin";
import {VcDateUtils} from "/src/visualcrossing-core/vcdateutils";
import CommonModals from '/src/components/CommonModals.vue';
import '/src/website-core/VcUtils';
import * as d3 from "d3";

export default {
  name: 'serverminitoring',
  props: {
    
  },
  data() {
    return {
      // Modal state management
      modals: {
        serverStatus: { isOpen: false },
        serverLog: { isOpen: false },
        serverJobViewer: { isOpen: false }
      },

      // Your existing data properties
      ALLLOGLEVELS:["ERROR","WARN","INFO"],
      servers:[
          {id:"Weather1", name:"Weather 1", serverroot:"https://weather1.visualcrossing.com/VisualCrossingWebServices/rest/services/", monitorData:{}},
          {id:"Weather2", name:"Weather 2", serverroot:"https://weather2.visualcrossing.com/VisualCrossingWebServices/rest/services/", monitorData:{}},
          {id:"Weather3", name:"Weather 3", serverroot:"https://weather3.visualcrossing.com/VisualCrossingWebServices/rest/services/", monitorData:{}},
          {id:"Weather4", name:"Weather 4", serverroot:"https://weather4.visualcrossing.com/VisualCrossingWebServices/rest/services/", monitorData:{}},
          {id:"Weather5", name:"Weather 5", serverroot:"https://weather5.visualcrossing.com/VisualCrossingWebServices/rest/services/", monitorData:{}},
          {id:"Weather6", name:"Weather 6", serverroot:"https://weather6.visualcrossing.com/VisualCrossingWebServices/rest/services/", monitorData:{}},
        ],
      refreshinterval:10,
      enableautorefresh:true,
      sessionState:null,
      lastrefresh:0,
      epochStart:1609459200,
      statisticsMins:[5,60,720],
      selectedServer:null,
      historyFilterSecsAgo:10800,
      resolution:500,
      serverLog:[],
      serverLogServer:null,
      maxJobDisplayCount:250,
      logLevels:[],
      logFilter:null,
      waitingForSessionAttempt:true,
    }
  },
  methods:{
    // New modal control methods
    openModal(modalName) {
      this.modals[modalName].isOpen = true;
    },

    closeModal(modalName) {
      this.modals[modalName].isOpen = false;
    },

    closeAllModals() {
      Object.keys(this.modals).forEach(key => {
        this.modals[key].isOpen = false;
      });
    },

    // Specific modal opening methods
    openServerStatusModal(server) {
      this.selectedServer = server;
      this.openModal('serverStatus');
    },

    openServerLogModal(server) {
      this.refreshLog(server);
      this.openModal('serverLog');
    },

    openServerJobViewerModal(server) {
      this.selectedServer = server;
      this.openModal('serverJobViewer');
    },

    // Handle ESC key
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        // Find and close the first open modal
        const openModal = Object.keys(this.modals).find(key => this.modals[key].isOpen);
        if (openModal) {
          this.closeModal(openModal);
        }
      }
    },

    // Your existing methods stay exactly the same
    setLogFilter(level) {
      var me=this;
      let index= me.logLevels.indexOf(level);
      if (index>=0) {
        me.logLevels.splice(index,1);
      } else {
        me.logLevels.push(level);
      }
    },

    getStatusText(status) {
      switch (status) {
        case 0: return "Unknown";
        case 1: return "Queued";
        case 2: return "Executing";
        case 3: return "Complete";
        case 4: return "Error";
        case 5: return "Cancelled";
        default: return "? " +status;
      }
    },

    errorCount(server) {
      let levelCounts=server && server.statusDetailed && server.statusDetailed.levelCounts;
      if (!levelCounts) return "-";
      return levelCounts.fatal+levelCounts.error;
    },

    warnCount(server) {
      let levelCounts=server && server.statusDetailed && server.statusDetailed.levelCounts;
      if (!levelCounts) return "-";
      return levelCounts.warn;
    },

    refreshLog(server) {
      var me=this;
      me.serverLog=[];
      me.serverLogServer=server;
      if (!me.isAdmin) return;
      vcCommunicator.serverlog(server.serverroot)
          .then(function (log) {
            if (!log.content) return;
            let lines=log.content.split("\n");

            lines=lines.map(function(d,i) {
              if (i===0) return null;
              let row= d.split("\t");
              while (row.length<3) {
                row.splice(row,0,"")
              }
              return row;
            });
            lines=lines.filter(function(d) {
              return d!==null;
            })
            me.serverLog=lines;
          })
          .catch((error) => {
             me.serverLog=error;
          });
    },

    getActiveJobCount(server) {
        if (!server || !server.monitorData || !server.monitorData.jobs) return "-";
        return server.monitorData.jobs.length;
    },

    // Remove the old toggleModal method since we're using Vue state now
    
    getTime:function() {
        return new Date().getTime();
    },

    getTimeToEpoch:function() {
       return (this.getTime()/1000)-this.epochStart;
    },

    format:function(v,d,suffix) {
        if (!v && v!==0) return " - ";
        if (d) v=v.toFixed(d);
        if (suffix) return v+suffix;
        return v;
    },

    formatGb(v) {
        return this.format(v/1024, 1)
    },

    // ... rest of your existing methods stay exactly the same
    refreshMonitor:function() {
      var me=this;
      if (!me.isAdmin) return;
      var sinceLastRefresh=me.lastrefresh && Math.ceil((me.getTime()-me.lastrefresh)/1000);
      var servers=me.servers;
      var secondsBack=sinceLastRefresh>0 && sinceLastRefresh<120?sinceLastRefresh:0;

      servers.forEach(function(server, index) {
        vcCommunicator.monitor(secondsBack, server.serverroot)
          .then(function (results) {
            var existingMonitorEvents=server && server.monitorData && server.monitorData.monitors;
            results.freememorypercent=0;
            results.jobSummary={};
            results.jobErrors={};
            server["monitorData"]=results;
            me.lastrefresh=me.getTime();
            me.processMonitorData(server, existingMonitorEvents);
            me.refreshHistoryChart(server);
          })
          .catch((error) => {
            console.log(error);
          });

        vcCommunicator.serverstatus(server.serverroot)
          .then(function (results) {
            server.status=results.status;
            server.statusDetailed=results;
            me.lastrefresh=me.getTime();
          })
          .catch((error) => {
            console.log(error);
            server.status="COMM_ERR";
            server.statusDetailed={};
            me.lastrefresh=me.getTime();
          });
      });
      
      if (me.enableautorefresh) {
        setTimeout(function() {
          me.refreshMonitor()
        }, me.refreshinterval*1000);
      }
    },

    processMonitorData:function(server, existingMonitorEvents) {
      var me=this;
      
      if (!server || ! server.monitorData) return;
      var  monitorData= server.monitorData;
      monitorData.freememorypercent=Math.round(100*monitorData.freeMemory/monitorData.totalMemory);

      if (!monitorData || !monitorData.monitors || monitorData.monitors.length===0) return;
      
      var monitors=monitorData.monitors;
      if (existingMonitorEvents && existingMonitorEvents.length>0) {
        var oldIndex=existingMonitorEvents.length-1;
        var oldestNewTime=monitors[0].time;
        while (oldIndex>=0 && existingMonitorEvents[oldIndex].time>oldestNewTime) {
          oldIndex--;
        }
        if (oldIndex>=0) {
          existingMonitorEvents=existingMonitorEvents.slice(0,oldIndex);
          monitors.splice(0, 0, ...existingMonitorEvents);
        }
      }

      var jobCountsByUser=monitorData.jobs.reduce(function(a,d) {
        var user=d.info;
        if (!d || !user || d.name!=="WeatherRequest") return a;
        var counts;
        if (d.execTime===-1 && d.status===5) { //cancelled
          counts=a.errors;
        } else {
          counts=a.usage;
        }

        if (!counts[user]) {
          counts[user]={
            id:user,
            count:1,
          };
        } else {
          counts[user].count++;
        }
        return a;
      }, {usage:{}, errors:{}});
      
      var sortedUsage=Object.values(jobCountsByUser.usage);
      sortedUsage.sort(function(a, b){return b.count - a.count});

      var sortedErrors=Object.values(jobCountsByUser.errors);
      sortedErrors.sort(function(a, b){return b.count - a.count});

      monitorData.jobSummary=sortedUsage.slice(0,3);
      monitorData.jobErrors=sortedErrors.slice(0,3);


      var now=me.getTimeToEpoch();
      
      var stats={};
      me.statisticsMins.forEach(function(d) {
        stats[d]={
          success:0,
          error:0,
          execTime:0,
          errorRate:0,
          meanTime:0,
        }
      });
      //Vue.set(monitorData, "stats", stats) 
      monitorData["stats"]=stats;
      var epochNow=me.getTimeToEpoch();
      var basetime=me.historyFilterSecsAgo?(epochNow-me.historyFilterSecsAgo) : monitors[0].time; //me.getStartOfDayToEpoch(); //
      //var timespan=me.historyFilterSecsAgo || (epochNow-monitors[0].time);
      var timeperbin=me.historyFilterSecsAgo?me.historyFilterSecsAgo/me.resolution:120;
      //console.log("Timeperbin:"+timeperbin);
      var currentBinIndex=-1, currentBin;

      monitorData.bins= monitors.reduce(function(a, d, i) {
        var binIndex=Math.floor((d.time-basetime)/timeperbin);
        if (binIndex<0) return a;
        if (!currentBin || currentBinIndex!==binIndex) {
          currentBin={
            time:binIndex*timeperbin+basetime,
            totalerrors:d.unexpected+d.license+d.handled+d.unknown,
            unhandlederrors:d.unexpected+d.unknown,
            licenseerrors:d.license,
            handlederrors:d.handled,
            success:d.success,
            successExecTime:d.successExecTime
          };
          a.push(currentBin);
          currentBinIndex=binIndex;
        } else {
          currentBin.totalerrors+=d.unexpected+d.license+d.handled+d.unknown;
          currentBin.unhandlederrors+=d.unexpected+d.unknown,
          currentBin.licenseerrors+=d.license;
          currentBin.handlederrors+=d.handled;
          currentBin.success+=d.success;
          currentBin.successExecTime+=d.successExecTime;
        }
        return a;
      }, []);
      
      monitorData.bins.forEach(function(d, i) {
        d.successpersecond=d.success/timeperbin;
        d.meanExecTime=d.successExecTime/d.success;

        me.statisticsMins.forEach(function(min, i) {
          if (!monitorData.stats) return;
          var s=monitorData.stats[min];
          
          if (s && (!min ||  d.time>(now-min*60))) {
            s.success+=d.success;
            s.error+=d.unhandlederrors;
            s.execTime+=d.successExecTime;
          }

        });
      });

      me.statisticsMins.forEach(function(d, i) {
        if (!monitorData.stats) return;
        var s=monitorData.stats[d];
        if (s) {
          s.errorRate=100*s.error/(s.success+s.error);
          s.meanTime=Math.round(s.execTime/s.success);
        }
      });

    },

    countLogByLevel(level) {
      var me=this;
      var serverLog=me.serverLog;
      if (!serverLog) return 0;
      return serverLog.reduce(function(a,d) {
        return d[1]===level?a+1:a;
      },0);
    },

        refreshHistoryChart:function(server) {
        var me=this;

        if (!server.monitorData) return;

        var svg = d3.select("."+server.id+" .activitychart");
        
        if (!svg || !svg.node()) return;

        var rect=svg.node().getBoundingClientRect();
  
        
        var margin = {
          top: 10,
          right: 30,
          bottom: 20,
          left: 30
        },
        width = rect.width - margin.left - margin.right,
        height =rect.height - margin.top - margin.bottom;
  
        //var data=server.monitorData.monitors;
        var data=server.monitorData.bins;
        if (!data || data.length===0) return;

        var minTime;
     
        if (me.historyFilterSecsAgo) {
          minTime=me.getTimeToEpoch()-me.historyFilterSecsAgo;
        } else {
          minTime=Math.min(data[0].time,me.getTimeToEpoch()-3600);
        }
        data=data.filter(function(d) {
          return d.time>=minTime;
        })

        //minTime=me.getTimeToEpoch();
        var x = d3.scaleLinear()
          .range([0, width])
          .domain([minTime, me.getTimeToEpoch()]);


        var axisx=x;

        var errorBandWidth=width*10/(x.domain()[1]-x.domain()[0]);
        if (errorBandWidth<1) errorBandWidth=2;
        //var xerr = d3.scaleLinear()
        //  .range([0, width])
        //  .domain(x.domain());

        
        var y = d3.scaleLinear()
         .range([height, 0])
         .domain([0,  d3.max(data, function (d) {
              return d.successpersecond;
            })]);
            
        var yerr = d3.scaleLinear()
            .range([height,0])
            .domain([0,  1.5*d3.max(data, function (d) {
                 return d.totalerrors || 1;
               })]);
        
        var yexectime = d3.scaleLinear()
          .range([height, 0])
          .domain([0,  d3.max(data, function (d) {
              return d.meanExecTime;
            })]);


        
        var g;
        var transitionDuration=500;

        if (svg.select("g").empty())  {
          g = svg.append("g")
            .attr("clip-path","url(#chart_"+server.id+")")
            .attr("class", "chartcontainer");
          g=g.append("g")
            .attr("class", "chart");
            
          g.attr("transform", "translate(" + margin.left + "," + margin.top + ")");
          g.node()._xdomain=x;
          g.node()._historyFilterSecsAgo=me.historyFilterSecsAgo
          svg.append("g")			
            .attr("class", "time-axis")
          svg
            .append("g")
            .attr("class", "y-axis")
    
          svg
            .append("g")
            .attr("class", "yerr-axis");

          svg.append("clipPath")
            .attr("id", "chart_"+server.id)
            .append("rect")
              .attr("class", "clippath");
        } else {
          g=svg.select(".chart");
          
          if (g.node()._xdomain && g.node()._historyFilterSecsAgo===me.historyFilterSecsAgo) {
            var offset=-g.node()._xdomain(x.domain()[0]);
            transitionDuration=me.refreshinterval*1000;
            g
            //.transition()
            //  .ease(d3.easeLinear)
             // .duration(transitionDuration)
              .attr("transform", "translate(" + (offset+margin.left) + "," + margin.top + ")");
            x=g.node()._xdomain;
          } else {
            g.attr("transform", "translate(" + margin.left + "," + margin.top + ")");
            g.node()._xdomain=x;
            g.node()._historyFilterSecsAgo=me.historyFilterSecsAgo
          }
        }

        svg.select(".clippath")
            .attr("x",margin.left)
            .attr("y", margin.top)
            .attr("height",height)
            .attr("width",width)
      


        var errorColors={
          "unhandlederrors":"#d73027",
          //"handlederrors":"#4eb3d3",
          //"licenseerrors":"#2166ac"
        }
        var u;
        var stackGen = d3.stack().keys(["unhandlederrors",
                                  // "handlederrors", 
                                  //"licenseerrors"
                                ]);

        u=g.selectAll(".joberror")
          .data(stackGen(data.filter(function(d) {
            return d.totalerrors>0;
          })))
        u.exit().remove();

        u=u.enter().append("g")
          .attr("class", "joberror")
          .merge(u)
        
        u.attr("fill", function(d) { return errorColors[d.key]; })
     
        u=u.selectAll("rect")
          .data(function(d) { 
            return d; 
          }),function (d) {
            return d.data.time;
          };

        u.exit().remove();
        u=u.enter().append("rect")
          .merge(u)

        u.attr("x", function (d) {
              return x(d.data.time)-errorBandWidth/2;
            })
            .attr("y", function (d) {
              return yerr(d[1]);
            })
            .attr("width", errorBandWidth)
            .attr("height", function (d) {
              return  yerr(d[0]) - yerr(d[1]);
            });

        
  
        var successline = d3.line()
            .defined(function(d) { return d.successpersecond;})
            .x(function(d) { return x(d.time); })
            .y(function(d) { return y(d.successpersecond); }) 
           // .curve(d3.curveMonotoneX);


        u=g.selectAll(".success")
          .data([data]);
        
        u.enter()
          .append("path")
          .attr("class", "success") 
          .merge(u)
          .datum(data) 
          .attr("d", successline); 
          
          var exectTimeLine = d3.line()
            .defined(function(d) { return d.meanExecTime;})
            .x(function(d) { return x(d.time); })
            .y(function(d) { return yexectime(d.meanExecTime); }) 
            //.curve(d3.curveMonotoneX);


          u=g.selectAll(".exectime")
            .data([data]);
          
          u.enter()
            .append("path")
            .attr("class", "exectime") 
            .merge(u)
            .datum(data) 
            .attr("d", exectTimeLine); 
      
      var timeDelta=axisx.domain()[1]-axisx.domain()[0];
      var axisTicks=[];
      var time=new Date((me.epochStart+axisx.domain()[0])*1000);
      time.setHours(time.getHours()-1,0,0,0);
      time=time.getTime()/1000;
      if (timeDelta<=600) {
        timeDelta=60; //
      } else if (timeDelta<=3600) {
        timeDelta=600; //
      } else if (timeDelta<10800) {
        timeDelta=1800; //
      } else {
        timeDelta=3600;
      }
      var endTime=axisx.domain()[1]+me.epochStart
      while (time<=endTime) {
        time+=timeDelta;
        axisTicks.push(time-me.epochStart);
      }

       
      svg.select(".time-axis").attr("transform", "translate("+margin.left+","+(height+margin.top)+")")
            .call(d3.axisBottom(axisx)
               .tickValues(axisTicks)
               .tickSize(-5)
               .tickFormat(function(d) {
                      return VcDateUtils.formatTime(d+me.epochStart);
                  })
            )

      svg.select(".y-axis").attr("transform", "translate("+margin.left+","+margin.top+")")
        .call(d3.axisLeft(y ).ticks(5));

      svg.select(".yerr-axis").attr("transform", "translate("+(margin.left+width)+","+margin.top+")")
        .call(d3.axisRight(yerr).ticks(5));
      },
  },

  computed:{
    // ... all your existing computed properties stay the same
    isAdmin() {
      let me=this;
      return me.sessionState && me.sessionState.contextProperties &&  me.sessionState.contextProperties.isAdmin==="true";
    },

    selectedServerJobs() {
      var me=this;
      if (!me.selectedServer || !me.selectedServer.monitorData) return [];
      return me.selectedServer.monitorData.jobs;
    },

    filteredServerJobs() {
      var me=this;
      if (!me.selectedServerJobs) return [];
      let jobs=me.selectedServerJobs.slice(0,me.maxJobDisplayCount);
      return jobs;
    },

    detailedServerStatus() {
      var me=this;
      if (!me.selectedServer || !me.selectedServer.statusDetailed) return null;
      let props=Object.keys(me.selectedServer.statusDetailed);
      props.sort();
      return props.reduce(function(a, d) {
            a.push({name:d, value:me.selectedServer.statusDetailed[d]})
            return a;
      }, []);
    },

    logErrors() {
      return this.countLogByLevel('ERROR');
    },

    logWarnings() {
       return this.countLogByLevel('WARN');
    },

    logInfos() {
       return this.countLogByLevel('INFO');
    },

    filteredServerLog() {
      var me=this;
      var serverLog=me.serverLog;
      if (!serverLog) return [];
      return serverLog.filter(function(d) {
        if (!d[2]) return false;
        if (me.logFilter && !d[2].includes(me.logFilter)) return false;
        if (me.logLevels && me.logLevels.length>0 && !me.logLevels.includes(d[1])) return false;
        return true;
      });
    }
  },

  mounted() {
    var me=this;
   
    // Add ESC key listener
    document.addEventListener('keydown', this.handleKeyDown);

    var vcCommunicator=window.vcCommunicator;
    if (!vcCommunicator) {
      console.log("No vcCommunicator");
      return;
    }

    vcCommunicator.waitForSession(function (sessionState, error) {
      if (error) {
        console.log(`Failed to attach session... ${error}`);
      }
      me.sessionState=sessionState;
      me.refreshMonitor();
      me.waitingForSessionAttempt=false;
    });

    vcCommunicator.waitForTerminateSession(function () {
      me.sessionState=null;
    });
  },

  beforeUnmount() {
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeyDown);
  },

  components: {
    CommonModals
  }
}
</script>

<style scoped>
/* Modal styles to ensure proper display without Bootstrap JS */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.modal.show {
  display: block !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: #000;
}

.modal-backdrop.show {
  opacity: 0.5;
}

.modal-dialog {
  position: relative;
  z-index: 1060;
}

/* Your existing styles stay the same */
.subtitle {
  color:#d5d2dc
}

.activitychart {
    background-color: #fff;
}

.activitychart :deep() .success {
    stroke:rgb(26, 150, 26);
    fill:none;
}

.activitychart :deep() .exectime {
    stroke:rgb(62, 66, 62);
    fill:none;
}


.activitychart :deep() .error {
    fill:rgb(128, 4, 0);
}

.activitychart :deep() .unexpectederror {
    stroke:rgb(128, 4, 0);
    fill:none;
}
.activitychart :deep() .licenseerror {
    stroke:rgb(243, 231, 65);
    fill:none;
}
.activitychart :deep() .handlederror {
    stroke:rgb(133, 82, 25);
    fill:none;
}
</style>